'use client';

import { useEffect, useMemo, useState } from 'react';
import { Button, buttonClasses } from '@/components/Button';
import { Card } from '@/components/Card';
import { Field, Input, Select } from '@/components/Input';
import { NeedsAuth } from '@/components/NeedsAuth';
import { PageTitle } from '@/components/PageTitle';
import { getClient } from '@/lib/api';
import { eur, shortDate } from '@/lib/fmt';
import type { components } from '@/lib/api.types';

type Rule = components['schemas']['RuleOut'];
type PreviewResponse = components['schemas']['RulePreviewResponse'];

const MATCH_KEYS = [
  'merchant',
  'merchant_contains',
  'merchant_regex',
  'description_contains',
  'description_regex',
  'source',
  'direction',
  'amount',
  'amount_min',
  'amount_max',
] as const;
type MatchKey = (typeof MATCH_KEYS)[number];

const NUMERIC_KEYS = new Set<MatchKey>(['amount', 'amount_min', 'amount_max']);

const PLACEHOLDERS: Record<MatchKey, string> = {
  merchant: 'Starbucks',
  merchant_contains: 'starbucks',
  merchant_regex: '(?i)\\bstarbucks\\b',
  description_contains: 'salário',
  description_regex: '(?i)payroll|wages',
  source: 'revolut | paypal | novobanco',
  direction: 'debit | credit',
  amount: '9.99',
  amount_min: '5',
  amount_max: '20',
};

type Condition = { id: number; key: MatchKey; value: string };

let CONDITION_ID = 0;
const newCondition = (key: MatchKey, value = ''): Condition => ({
  id: CONDITION_ID++,
  key,
  value,
});

export default function RulesPage() {
  return (
    <NeedsAuth>
      <Inner />
    </NeedsAuth>
  );
}

function Inner() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const client = getClient();
    if (!client) return;
    void (async () => {
      if (!cancelled) setError(null);
      const { data, error } = await client.GET('/api/rules');
      if (cancelled) return;
      if (error) setError(JSON.stringify(error));
      else setRules(data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const grouped = useMemo(() => {
    return {
      user: rules.filter((r) => r.source === 'user'),
      default: rules.filter((r) => r.source === 'default'),
    };
  }, [rules]);

  return (
    <div className="flex flex-col gap-6">
      <PageTitle>Rules</PageTitle>

      {error && (
        <Card>
          <p className="text-sm text-negative">Error: {error}</p>
        </Card>
      )}

      <NewRuleCard onSaved={() => setTick((t) => t + 1)} />

      <Card title={`Your rules (${grouped.user.length})`}>
        {grouped.user.length === 0 ? (
          <p className="py-4 text-center text-sm text-fg-muted">
            No user rules yet. Use the form above to add one, or relabel a transaction
            on the Transactions page (with &ldquo;learn=true&rdquo;) to derive one
            automatically.
          </p>
        ) : (
          <RulesTable
            rules={grouped.user}
            deletable
            onDelete={() => setTick((t) => t + 1)}
            onError={setError}
          />
        )}
      </Card>

      <Card title={`Default rules (${grouped.default.length})`} hint="bundled, read-only">
        <RulesTable rules={grouped.default} />
      </Card>
    </div>
  );
}

function RulesTable({
  rules,
  deletable,
  onDelete,
  onError,
}: {
  rules: Rule[];
  deletable?: boolean;
  onDelete?: () => void;
  onError?: (m: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-fg-muted">
          <tr>
            <th className="py-2 pr-4 font-medium">Name</th>
            <th className="py-2 pr-4 font-medium">Match</th>
            <th className="py-2 pr-4 font-medium">Category</th>
            {deletable && <th className="py-2 font-medium" />}
          </tr>
        </thead>
        <tbody>
          {rules.map((r) => (
            <tr key={`${r.source}:${r.name}`} className="border-t border-hairline">
              <td className="py-2 pr-4 font-mono text-xs text-fg-primary">{r.name}</td>
              <td className="py-2 pr-4">
                <code className="font-mono text-xs text-fg-muted">
                  {Object.entries(r.match)
                    .map(
                      ([k, v]) =>
                        `${k}=${typeof v === 'string' ? `"${v}"` : JSON.stringify(v)}`,
                    )
                    .join('  ')}
                </code>
              </td>
              <td className="py-2 pr-4 text-fg-primary">{r.category}</td>
              {deletable && (
                <td className="py-2 text-right">
                  <button
                    onClick={async () => {
                      if (!confirm(`Delete rule "${r.name}"?`)) return;
                      const client = getClient();
                      if (!client) return;
                      const { error } = await client.DELETE('/api/rules/{name}', {
                        params: { path: { name: r.name } },
                      });
                      if (error) onError?.(JSON.stringify(error));
                      else onDelete?.();
                    }}
                    className="text-xs text-fg-muted hover:text-negative"
                  >
                    delete
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewRuleCard({ onSaved }: { onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [conditions, setConditions] = useState<Condition[]>(() => [
    newCondition('merchant_contains'),
  ]);
  const [category, setCategory] = useState('');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const usedKeys = useMemo(() => new Set(conditions.map((c) => c.key)), [conditions]);
  const conditionsValid = conditions.every((c) => c.value.trim() !== '');

  function updateCondition(id: number, patch: Partial<Condition>) {
    setConditions((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }
  function removeCondition(id: number) {
    setConditions((cs) => (cs.length > 1 ? cs.filter((c) => c.id !== id) : cs));
  }
  function addCondition() {
    const next = MATCH_KEYS.find((k) => !usedKeys.has(k)) ?? 'merchant_contains';
    setConditions((cs) => [...cs, newCondition(next)]);
  }

  function buildMatch(): Record<string, unknown> | null {
    if (!conditionsValid) return null;
    const m: Record<string, unknown> = {};
    for (const c of conditions) {
      const raw = c.value.trim();
      if (NUMERIC_KEYS.has(c.key)) {
        const n = Number(raw);
        if (!Number.isFinite(n)) return null;
        m[c.key] = n;
      } else {
        m[c.key] = raw;
      }
    }
    return m;
  }

  function resetForm() {
    setOpen(false);
    setName('');
    setConditions([newCondition('merchant_contains')]);
    setCategory('');
    setPreview(null);
    setSubmitError(null);
  }

  async function runPreview() {
    const match = buildMatch();
    if (!match) {
      setSubmitError('Each condition needs a value (numbers must be valid).');
      return;
    }
    setBusy(true);
    setSubmitError(null);
    const client = getClient();
    if (!client) return;
    const { data, error } = await client.POST('/api/rules/preview', {
      body: { match, category, days: 90, limit: 25 },
    });
    setBusy(false);
    if (error) setSubmitError(JSON.stringify(error));
    else setPreview(data ?? null);
  }

  async function save() {
    const match = buildMatch();
    if (!name.trim() || !category.trim() || !match) {
      setSubmitError('Name, category, and at least one valid condition are required.');
      return;
    }
    setBusy(true);
    setSubmitError(null);
    const client = getClient();
    if (!client) return;
    const { error } = await client.POST('/api/rules', {
      body: {
        name: name.trim(),
        match,
        category: category.trim(),
      },
    });
    setBusy(false);
    if (error) {
      setSubmitError(JSON.stringify(error));
      return;
    }
    resetForm();
    onSaved();
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={buttonClasses('ghost') + ' h-auto !rounded-md border border-dashed border-hairline py-4 !font-medium'}
      >
        + New rule
      </button>
    );
  }

  return (
    <Card title="New rule">
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my-cool-rule"
              className="font-mono"
            />
          </Field>
          <Field label="Category">
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Food & Drink/Coffee"
              className="font-mono"
            />
          </Field>
        </div>
        <Field label="Match conditions" hint="all must match (AND)">
          <div className="flex flex-col gap-2">
            {conditions.map((c, idx) => {
              const isNumeric = NUMERIC_KEYS.has(c.key);
              return (
                <div key={c.id} className="flex flex-wrap items-center gap-2">
                  <Select
                    value={c.key}
                    onChange={(e) =>
                      updateCondition(c.id, { key: e.target.value as MatchKey })
                    }
                    className="font-mono"
                    aria-label={`Match key ${idx + 1}`}
                  >
                    {MATCH_KEYS.map((k) => (
                      <option
                        key={k}
                        value={k}
                        disabled={k !== c.key && usedKeys.has(k)}
                      >
                        {k}
                      </option>
                    ))}
                  </Select>
                  <Input
                    value={c.value}
                    onChange={(e) => updateCondition(c.id, { value: e.target.value })}
                    placeholder={PLACEHOLDERS[c.key]}
                    type={isNumeric ? 'number' : 'text'}
                    inputMode={isNumeric ? 'decimal' : undefined}
                    step={isNumeric ? '0.01' : undefined}
                    className="flex-1 font-mono"
                    aria-label={`Match value ${idx + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeCondition(c.id)}
                    disabled={conditions.length === 1}
                    className="text-xs text-fg-muted transition-colors hover:text-negative disabled:opacity-40 disabled:hover:text-fg-muted"
                    aria-label={`Remove condition ${idx + 1}`}
                  >
                    remove
                  </button>
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button
              variant="ghost"
              onClick={addCondition}
              disabled={usedKeys.size >= MATCH_KEYS.length}
            >
              + Add condition
            </Button>
            <Button
              variant="secondary"
              onClick={runPreview}
              disabled={busy || !conditionsValid}
              className="ml-auto"
            >
              Preview matches
            </Button>
          </div>
        </Field>

        {preview && (
          <div className="rounded-md bg-sunken p-3 text-xs">
            <div className="mb-2 font-medium text-fg-primary">
              {preview.match_count} match{preview.match_count === 1 ? '' : 'es'} in last
              90 days
            </div>
            {preview.sample.length > 0 && (
              <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                {preview.sample.map((m) => (
                  <li key={m.transaction_id} className="flex justify-between gap-3">
                    <span className="tnum text-fg-muted">{shortDate(m.booked_at)}</span>
                    <span className="line-clamp-1 flex-1 text-fg-primary">
                      {m.merchant ?? m.description}
                    </span>
                    <span className="tnum font-mono">{eur(m.amount)}</span>
                    <span className="text-fg-muted">{m.current_category ?? '—'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          {submitError && (
            <span className="mr-auto text-xs text-negative">{submitError}</span>
          )}
          <Button variant="ghost" onClick={resetForm}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={save}
            disabled={!name.trim() || !category.trim() || !conditionsValid}
            loading={busy}
          >
            Save rule
          </Button>
        </div>
      </div>
    </Card>
  );
}
